<template>
  <component
    v-if="reviews.total > 0"
    :is="conditionalElement"
    v-bind="conditionalProps('#ReviewsWidget')"
    class="ReviewStars"
  >
    <div class="ReviewStars__Wrapper" :hidden="reviews.total > 0">
      <div class="ReviewStars__Bottom">
        <Icon :icon="'star'" v-for="i in stars" :key="i" />
      </div>
      <div
        class="ReviewStars__Top"
        :style="{ clip: 'rect(0px,' + starTopWidth + 'px, 50px, 0px)' }"
      >
        <Icon :icon="'star'" v-for="i in stars" :key="i" />
      </div>
      <p class="ReviewStars__Total p5">({{ total }})</p>
      <div v-if="ygroup" class="ReviewStars__Tooltip">
        <button
          ref="tooltipButton"
          class="ReviewStars__Tooltip-Button"
          aria-label="Review information"
          aria-describedby="review-tooltip-content"
          @click="toggleTooltip($event)"
          @mouseenter="toggleTooltip($event)"
          @mouseleave="toggleTooltip($event)"
        >
          <Icon :icon="'info-tooltip'" :aria-hidden="true" />
        </button>
        <!-- Tooltip content with dynamic positioning from Floating UI -->
        <!-- data-placement controls arrow CSS styles (top/bottom/left/right) -->
        <content
          ref="tooltipContent"
          class="ReviewStars__Tooltip-Content"
          role="tooltip"
          id="review-tooltip-content"
          :aria-hidden="tooltipToggle"
          :style="floatingStyles"
          :data-placement="arrowSide"
        >
          <!-- Arrow element positioned by Floating UI's arrow middleware -->
          <!-- Arrow direction is styled via ::before and ::after in CSS based on data-placement -->
          <div
            ref="tooltipArrow"
            class="ReviewStars__Tooltip-Arrow"
            :style="arrowStyles"
          ></div>
          <p class="p4">
            Includes reviews for the original and updated versions of this
            product.
          </p>
          <button
            @click="toggleTooltip($event)"
            aria-label="Close tooltip"
            class="ReviewStars__Tooltip-Close"
          >
            <Icon :icon="'close-tooltip'" :aria-hidden="true" />
          </button>
        </content>
      </div>
    </div>
  </component>
  <span v-if="location == 'quick-view'" class="placeholder"></span>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
// Import Floating UI utilities including 'arrow' for tooltip arrow positioning
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  arrow,
} from '@floating-ui/vue';
import Icon from './Icon.vue';

const props = defineProps({
  product: String,
  location: { type: String, required: false },
  ygroup: { type: Boolean, required: false },
});

const key = 'MdtpPOdzecvdDWwA3pFvhLyB6ab4osWf5p8TQHpj';

// Yotpo API Docs
// https://apidocs.yotpo.com/reference/retrieve-reviews-for-a-product
const endpoint = `https://api-cdn.yotpo.com/v1/widget/${key}/products/${props.product}/reviews.json`;

const stars = ref(5);
const reviews = ref(false);
const tooltipToggle = ref(true);
const clickedEvent = ref(false);

// Floating UI setup - handles tooltip positioning and arrow alignment
const tooltipButton = ref(null); // Reference to the info button
const tooltipContent = ref(null); // Reference to the tooltip content container
const tooltipArrow = ref(null); // Reference to the arrow element inside tooltip

// Configure Floating UI with positioning middleware
// - placement: Extract placement data to determine which side the tooltip appears on
// - middlewareData: Contains calculated arrow position from the arrow middleware
const { floatingStyles, placement, middlewareData } = useFloating(
  tooltipButton,
  tooltipContent,
  {
    placement: 'bottom', // Default placement below the button
    middleware: [
      offset(16), // 16px gap between button and tooltip (includes space for arrow + margin)
      flip(), // Automatically flip to opposite side if there's not enough space
      shift({ padding: 8 }), // Keep tooltip within viewport with 8px padding
      arrow({ element: tooltipArrow }), // Calculate optimal arrow position
    ],
    whileElementsMounted: autoUpdate, // Update position when elements change (scroll, resize, etc.)
  }
);

// Extract the primary placement side (top/bottom/left/right) from placement value
// Used to apply the correct CSS styles for arrow direction via data-placement attribute
const arrowSide = computed(() => {
  return placement.value.split('-')[0];
});

// Calculate arrow position styles based on Floating UI's arrow middleware data
// The arrow middleware returns optimal x/y coordinates for centering the arrow
const arrowStyles = computed(() => {
  const arrowData = middlewareData.value.arrow;

  if (!arrowData) return {};

  const { x, y } = arrowData;

  // Offset by half the arrow width (8px) to center the arrow's point over the button
  // Without this offset, the left edge of the arrow would be centered instead of the point
  const offsetX = x != null ? x - 8 : null;
  const offsetY = y != null ? y - 8 : null;

  return {
    left: offsetX != null ? `${offsetX}px` : '',
    top: offsetY != null ? `${offsetY}px` : '',
  };
});

const conditionalProps = computed(() => {
  return (url) => {
    const isLink = conditionalElement.value === 'a';
    if (!isLink) return {};
    return {
      href: url,
    };
  };
});

const conditionalElement = computed(() => {
  return props.location == 'quick-view' || props.location == 'product-card'
    ? 'div'
    : 'a';
});

const starTopWidth = computed(() => {
  const rating = reviews.value.average;
  const percentage = rating * 10;
  const width = percentage + 26;
  return width;
});

const total = computed(() => {
  if (!!reviews.value) return reviews.value?.total.toLocaleString() || '';
  else return '';
});

function format(context) {
  const data = context || false;
  if (!data) return;

  const total = parseInt(data.total_review);
  const average = parseFloat(data.average_score);

  return {
    average,
    total,
  };
}

async function fetchReviews() {
  try {
    const reviewData = await fetch(endpoint)
      .then((res) => res.json())
      .then((json) => json.response.bottomline);

    reviews.value = format(reviewData);
    return;
  } catch (err) {
    console.log(err);
  }
}

const toggleTooltip = (e) => {
  e.preventDefault();
  switch (e.type) {
    case 'click':
      if (!tooltipToggle.value && !clickedEvent.value) {
        tooltipToggle.value = false;
        clickedEvent.value = true;
        break;
      } else {
        tooltipToggle.value = !tooltipToggle.value;
        clickedEvent.value = !clickedEvent.value;
        break;
      }
    case 'mouseenter':
      if (!clickedEvent.value) {
        tooltipToggle.value = false;
        break;
      }
    case 'mouseleave':
      if (!clickedEvent.value && !tooltipToggle.value) {
        tooltipToggle.value = true;
        break;
      }
  }
};

onMounted(() => {
  fetchReviews();
});
</script>
